<template>
  <MonitorWrapper class="pid-ind-app-wrap">
    <el-row :gutter="20" type="flex" align="middle">
      <el-col v-for="(item, groupIndex) in groups" :span="item.span" :key="`group-${groupIndex}`">
        <BorderComp3>
          <div
            class="chart-wrap"
            v-for="(v, i) in item.charts"
            :style="{ height: `${100 / item.charts.length}%` }"
            :key="`group${groupIndex}-item-${i}`"
          >
            <v-chart :options="v" :autoresize="true" />
          </div>
        </BorderComp3>
      </el-col>
    </el-row>
  </MonitorWrapper>
</template>

<script>
import { lineAreaStyle } from '@/echartOption/lineAreaStyle';
import { graColor } from '@/views/realdata/FCBR-100/options';
import singleDeviceMixin from '@/mixins/singleDeviceMixin';
import BorderComp3 from '@/components/border-comp3';
import { lowSceneLevel } from '@/utils/dict';
import { merge } from 'lodash';
import { isArray, isObject } from '@/utils/is';

export default {
  name: 'PidIndApp',
  components: {
    BorderComp3
  },
  mixins: [singleDeviceMixin],
  data() {
    return {
      wsData: {},
      curMod: 'tvocMod',
      chartMap: {},
      pidThreshold: 0,
      thresholdTask: null
    };
  },
  computed: {
    tvocMod() {
      const { mods = {} } = this.wsData;
      return mods.tvocMod || {};
    },
    charts() {
      return Object.values(this.chartMap) || [];
    },
    groups() {
      const charts = this.charts;
      if (charts.length === 1) {
        return [{ span: 24, charts: charts }];
      }
      if (charts.length === 2) {
        return [
          { span: 12, charts: charts[0] },
          { span: 12, charts: charts[1] }
        ];
      }
      if (charts.length >= 2) {
        return [
          { span: 12, charts: charts.slice(0, 2) },
          { span: 12, charts: charts.slice(2) }
        ];
      }
      return [];
    }
  },
  methods: {
    initChartMap(data) {
      this.$set(this, 'chartMap', data);
    },
    updateThreshold(newVal) {
      this.thresholdTask = code => {
        if (!this.chartMap[code]) return;
        if (!isArray(newVal.paramItems) && !isArray(newVal.data)) return;
        const items = newVal.paramItems || newVal.data;
        // 获取当前使用场景的 pidThreshould 值
        const res = items.find(e => (e.code || e.itemCode) === 'pidThreshold');
        const pidThreshold = res.value[lowSceneLevel[newVal.sceneCurrent]];
        const ptd = +pidThreshold;
        this.pidThreshold = ptd;
        this.chartMap[code] = merge(this.chartMap[code], {
          yAxis: {
            max(v) {
              v = v.max < 100 ? 2 * v.max : v.max + 100;
              return v < ptd ? Math.ceil(ptd * 1.2) : v;
            }
          },
          series: [
            {
              markLine: {
                data: [
                  {
                    name: '阈值',
                    yAxis: ptd,
                    lineStyle: {
                      color: '#FF4847'
                    }
                  }
                ]
              }
            }
          ],
          visualMap: {
            type: 'piecewise',
            top: 0,
            right: 10,
            orient: 'horizontal',
            textGap: 5,
            itemWidth: 14,
            itemHeight: 10,
            pieces: [
              { gt: -1, lte: ptd, color: '#03b36e', label: `0 - ${ptd}` },
              { gt: ptd, color: '#FF4847' }
            ],
            outOfRange: {
              color: '#FF4847'
            },
            textStyle: {
              color: '#00eeff',
              fontSize: 10
            }
          }
        });
      };
    },
    upChartMap(data) {
      const { mods, beginTime } = data;
      const tvocMod = mods.tvocMod;
      if (!tvocMod) return;
      const sensors = tvocMod.sensors;
      if (!Array.isArray(sensors)) return;
      let chart;
      const chartMap = Object.assign({}, this.chartMap);
      sensors.forEach(e => {
        chart = chartMap[e.code] =
          chartMap[e.code] ||
          this.makeOption({ title: e.name, unitName: e.unitName, threshold: this.pidThreshold });
        chart.series[0].data.push([beginTime, e.value]);
        if (chart.series[0].data.length > 20) {
          chart.series[0].data.shift();
        }
      });
      this.initChartMap(chartMap);
    },
    makeOption({ title, color = '#3368F2', unitName, dataZoom }) {
      return lineAreaStyle({
        color: color,
        title: { text: title, left: 'center' },
        dataZoom,
        yAxis: { name: unitName },
        series: [
          { areaStyle: { color: graColor('rgba(51, 104, 242, 0.5)', 'rgba(51, 104, 242, 0)') } }
        ]
      });
    },
    getSensorInfoBy(data, code, key) {
      if (!data || !code || !key) return;
      if (!data.mods || !isObject(data.mods.tvocMod)) return;
      const { sensors } = data.mods.tvocMod;
      if (!isArray(sensors)) return;
      const sensor = sensors.find(e => e.code === code);
      if (!isObject(sensor)) return;
      return sensor[key];
    },
    async initPage() {
      // 历史数据进入时
      if (this.isHisDetail) {
        const { data } = await this.getHistoryDetailData(this.eventId);
        this.wsData = data.deviceData;
        const { data: list, dimensions, dimensionsZh, deviceData, paramJSON } = data;
        const params = JSON.parse(paramJSON);
        let [timestampKey, ...sensors] = dimensions;
        const toTimestamp = v => new Date(v).getTime();
        const dict = { pid: 'VOC-10.6ev' };
        const chartMap = sensors.reduce((map, key, index) => {
          const k2 = dimensionsZh[index + 1];
          const unitName = this.getSensorInfoBy(deviceData, dict[k2] || k2, 'unitName') || '';
          const chart = this.makeOption({
            unitName,
            name: deviceData.name,
            dataZoom: [{ type: 'inside' }]
          });
          chart.series[0].data.push(...list.map(e => [toTimestamp(e[timestampKey]), e[key]]));
          map[dimensionsZh[index + 1]] = chart;
          return map;
        }, {});
        this.initChartMap(chartMap);
        params && this.updateThreshold(params);
        this.execTask(this.chartMap);
        this.hideLoading();
      }
    },
    updateData(newVal) {
      this.upChartMap(newVal);
    },
    execTask(data) {
      const codes = ['pid', 'VOC-10.6ev'];
      codes.forEach(code => {
        if (!data[code]) return;
        if (this.thresholdTask) {
          this.thresholdTask(code);
          this.thresholdTask = null;
        }
      });
    }
  },
  watch: {
    chartMap: 'execTask'
  },
  created() {
    this.initPage();
  }
};
</script>

<style lang="scss" scoped>
.pid-ind-app-wrap {
  position: relative;
  height: 100%;
  .el-row {
    height: 100%;
    .el-col {
      height: 100%;
    }
  }
  .state {
    @include state-textstyle;
  }
}
</style>
